import React from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Flex } from "antd"
import { Icon } from "ui/Icon"
import { formatExperienceTermsFull, getMaxGuests } from "utils/experience"
import ReadMoreText from "../../ReadMoreText"
import styles from "./TicketedDetails.module.css"

// "About" of a ticketed experience: category, guest limit, length and languages above the
// description. Languages are stored as option ids; their names come from the public experience
// options the page loads.
const TicketedAbout = ({ experience }) => {
  const { t } = useTranslation()
  const languageOptions = useSelector((state) => state.trips.experienceOptions?.data?.languages)
  const languageNameById = new Map((languageOptions || []).map((language) => [language.id, language.name]))
  const languages = (experience?.languages || [])
    .map((language) => (typeof language === "object" ? language?.name : languageNameById.get(language)))
    .filter(Boolean)
  const maxGuests = getMaxGuests(experience)
  const duration = formatExperienceTermsFull(experience, t)

  const facts = [
    maxGuests > 0 && { key: "guests", icon: "User", text: t("booking_widget.card.up_to_guests", { count: maxGuests }) },
    duration !== "—" && { key: "duration", icon: "Clock", text: duration },
    languages.length > 0 && { key: "languages", icon: "Globe", text: languages.join(" & ") }
  ].filter(Boolean)

  return (
    <Flex vertical gap={16} className={styles.section}>
      <Flex vertical gap={12}>
        <h2 className={styles.sectionTitle}>{t("global.about")}</h2>
        {experience?.category?.name && <span className={styles.categoryTag}>{experience.category.name}</span>}
        {facts.length > 0 && (
          <Flex vertical gap={4}>
            {facts.map(({ key, icon, text }) => (
              <Flex key={key} align="center" gap={8} className={styles.fact}>
                <Icon type="feather" name={icon} width={20} height={20} color="currentColor" />
                <span>{text}</span>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
      {experience?.description && <ReadMoreText text={experience.description} className={styles.description} />}
    </Flex>
  )
}

TicketedAbout.propTypes = {
  experience: PropTypes.shape({
    description: PropTypes.string,
    category: PropTypes.shape({ name: PropTypes.string }),
    languages: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object])),
    experience_type: PropTypes.string,
    ticket_types: PropTypes.arrayOf(PropTypes.shape({ enabled: PropTypes.bool, max_capacity: PropTypes.number })),
    duration_type: PropTypes.string,
    duration_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration_unit: PropTypes.string
  }).isRequired
}

export default TicketedAbout
